import React, { useEffect, useRef } from 'react';
import './Home.scss';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  MerchGrid,
  MerchGridItem,
  LoadingSpinner,
  LoadingSpinnerContainer,
  ErrorBox,
} from '@amzn/burberry-ui-components';
import { awsRum } from 'src/rum';
import { metricEmitter } from 'src/utils/metricEmitter';
import { AppDispatch } from 'src/store/store';
import { selectTemplate, selectUseTransparentImages } from 'src/store/templateSlice';
import {
  fetchCollectionV2,
  selectCollectionsV2,
  selectGetCollectionV2InProgress,
  selectMerchV2Error,
} from 'src/store/merchV2Slice';
import { selectCsrfToken, getValidateToken } from 'src/store/authSlice';
import { getStage } from 'src/utils/roadieHelper';
import { getActiveEngagementId } from 'src/utils/engagement';
import { buildProductUrl, UH_OH, GENERIC_ERROR_STRING } from '@amzn/d2c-application-website-common';
import { resolveImageUrl } from 'src/utils/imageUtil';

const PAGE_NAME = 'Home';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const template = useSelector(selectTemplate);
  const useTransparentImages = useSelector(selectUseTransparentImages);
  const collectionsV2 = useSelector(selectCollectionsV2);
  const getCollectionV2InProgress = useSelector(
    selectGetCollectionV2InProgress,
  );
  const merchV2Error = useSelector(selectMerchV2Error);
  const csrfToken = useSelector(selectCsrfToken);

  const pageLoadTime = useRef(Date.now());
  const pageLoadEmitted = useRef(false);
  const prevInProgress = useRef(false);
  const engagementId = getActiveEngagementId(template);
  const merchV2Items = engagementId ? (collectionsV2?.[engagementId] ?? []) : [];

  useEffect(() => {
    if (csrfToken) {
      dispatch(getValidateToken({ stage: getStage(), csrfToken }));
    }
  }, []);


  // Page view metric
  useEffect(() => {
    awsRum?.recordEvent('PageView', { page: PAGE_NAME });
  }, []);

  // Fetch merch collection — csrfToken intentionally excluded from deps since merch is unauthenticated;
  // logout re-fetch is handled explicitly by App.tsx after clearing the merch state
  useEffect(() => {
    if (engagementId) {
      dispatch(fetchCollectionV2({ engagementId, stage: getStage(), csrfToken }));
    }
  }, [dispatch, engagementId]);

  useEffect(() => {
    if (
      !pageLoadEmitted.current &&
      prevInProgress.current &&
      !getCollectionV2InProgress &&
      merchV2Items.length > 0
    ) {
      pageLoadEmitted.current = true;
      const loadTime = Date.now() - pageLoadTime.current;
      awsRum?.recordEvent("PageLoad", {
        page: PAGE_NAME,
        loadTimeMs: String(loadTime),
      });
    }
    prevInProgress.current = getCollectionV2InProgress;
  }, [getCollectionV2InProgress, merchV2Items.length]);

  if (getCollectionV2InProgress) {
    return (
      <div className="home-page home-page--loading" data-testid="home-page">
        <LoadingSpinnerContainer>
          <LoadingSpinner />
        </LoadingSpinnerContainer>
      </div>
    );
  }

  if (merchV2Error && merchV2Items.length === 0) {
    return (
      <div className="home-page" data-testid="home-page">
        <ErrorBox
          heading={UH_OH}
          message={GENERIC_ERROR_STRING}
          metricEmitter={metricEmitter}
        />
      </div>
    );
  }

  // Cap merch grid columns to 3 when page width is "center" for optimal UX.
  const MAX_CENTER_COLUMNS = 3;
  const merchGridColumns = template?.centerOrFullWidth === 'center' && template?.merchGridColumns != null
    ? Math.min(template.merchGridColumns, MAX_CENTER_COLUMNS)
    : template?.merchGridColumns;

  return (
    <div className="home-page" data-testid="home-page">
      <MerchGrid
        merchGridColumns={merchGridColumns}
        merchGridSpacing={template?.merchGridSpacing}
      >
        {merchV2Items.map((item: any) => {
          const { src: imageUrl, fallback: imageFallbackUrl } = resolveImageUrl(item.image, useTransparentImages);
          return (
            <MerchGridItem
              key={item.asin}
              imageUrl={imageUrl}
              imageFallbackUrl={imageFallbackUrl}
              imageAlt={item.name}
              title={item.name}
              price={item.price}
              onItemClick={() => navigate(buildProductUrl(item.asin, item.customId, item.customizationToken))}
              metricEmitter={metricEmitter}
            />
          );
        })}
      </MerchGrid>
    </div>
  );
};

export default Home;
